import { newFeatureFilter, newPropertyFilter } from '../request-filters';
import { getSourceFeatures, isFeatureClickable } from '../features';
import { boundingBoxIsLikePoint, newBoundingBoxOptions } from '../bounding-box';
import { newBoundingBoxEvent } from '../bounding-box';
import { wrapUpdateCall } from '../api';
import MaxZoomLevels from '../zoom-levels';
import { deactiveFeaturesForSource, filterLayerIfExists, removeApproxLocationCircle, removeLayersForSource, setFeatureStateIfSourceExists } from '../layers';
import LocationFeature from '../models/feature';

import {
    District,
    Entity,
    Island,
    Municipality,
    Neighbourhood,
    Province,
    Region,
} from '../../locations';
import { locationFilters } from '../../property/filters';

const handleMunicipalityLayerClick = async function( ctx, event ) {
    if ( ctx.abortController ) {
        ctx.abortController.abort();
        ctx.abortController = new AbortController();
    }

    const mapFeature = event.detail.feature;

    const isAlreadySelected = ( ctx.currentLocation && ctx.currentLocation.id && ctx.currentLocation.id === mapFeature.id );

    if ( !mapFeature || isAlreadySelected ) {
        return;
    }

    const feature = LocationFeature.fromMapBoxFeature( mapFeature );

    if ( !isFeatureClickable( feature ) ) {
        return;
    }

    ctx.setLoading( true );

    ctx.movefuncs = [];

    ctx.root.dispatchEvent( new CustomEvent( 'twc:collapse:collapse-all', { bubbles: true } ) );

    ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:remove-area', {
        bubbles: true,
        detail: {
            search: false,
        },
    } ) );

    ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:search-this-area:deactivate', { bubbles: true } ) );

    ctx.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:draw-your-search:deactivate', {
        bubbles: true,
        detail: {
            search: false,
        },
    } ) );

    [ ctx.baseForm, ctx.popupForm ].forEach( ( form ) => {
        locationFilters.all().forEach( ( name ) => {
            form.querySelectorAll( `[name=${name}]` ).forEach( ( input ) => input.value = '' );
        } );
    } );

    removeApproxLocationCircle( ctx.map );

    ctx.locationIndicator.update( feature.label( ctx.language ), {
        autohide: true,
        timeout: 5000,
    } );

    let fitBounds = false;

    const currentZoom = ctx.map.getZoom();

    if ( currentZoom < 12 && ctx.options.fitBoundsOnClick ) {
        fitBounds = true;
    }

    const featureParams = newFeatureFilter( {
        parentType: feature.type,
        parentID: feature.id,
    } );

    if ( ctx.currentLocation !== null ) {
        ctx.previousLocation = ctx.currentLocation;
    }

    ctx.currentLocation = mapFeature;

    removeLayersForSource( ctx.map, District );

    if ( ctx.map.getSource( District ) ) {
        ctx.map.removeSource( District );
    }

    removeLayersForSource( ctx.map, Neighbourhood );

    if ( ctx.map.getSource( Neighbourhood ) ) {
        ctx.map.removeSource( Neighbourhood );
    }

    removeLayersForSource( ctx.map, Entity );

    if ( ctx.map.getSource( Entity ) ) {
        ctx.map.removeSource( Entity );
    }

    let locationKeys = document.querySelectorAll( '.property-search-form [name=locationId]' );
    for ( let k = 0; k < locationKeys.length; k++ ) {
        locationKeys[ k ].dataset.latitude = feature.centerLatitude;
        locationKeys[ k ].dataset.longitude = feature.centerLongitude;
    }

    const propertyParams = newPropertyFilter( {}, ctx.baseForm );

    propertyParams.set( 'currentLocationID', feature.id );
    propertyParams.set( 'currentLocationType', Municipality );
    propertyParams.set( 'latitude', feature.centerLatitude );
    propertyParams.set( 'longitude', feature.centerLongitude );
    propertyParams.set( 'locationBoundSW', feature.bboxSouthWest );
    propertyParams.set( 'locationBoundNE', feature.bboxNorthEast );

    const municipalityIDs = feature.municipalityID;

    if ( municipalityIDs && municipalityIDs.length > 0 ) {
        for ( let i = 0; i < municipalityIDs.length; i++ ) {
            propertyParams.append( 'parentLocations', Municipality + ':' + municipalityIDs[ i ] );
        }
    }

    propertyParams.append( 'parentLocations', Municipality + ':' + feature.id );

    let metaOptions = {
        focusMapToSearch: fitBounds,
        tasks: [],
        appendFeaturesIfLayerExists: false,
        fetchPropertiesAfterAnimation: fitBounds,
    };

    let featureTypeToLoad = feature.nextChildFeatureType();

    if ( !propertyParams.has( 'radius' ) && fitBounds ) {
        let bboxOptions = newBoundingBoxOptions();

        if ( boundingBoxIsLikePoint( feature.bbox ) && feature.type in MaxZoomLevels ) {
            bboxOptions.maxZoom = MaxZoomLevels[ feature.type ];
        }

        const bboxEvent = newBoundingBoxEvent();

        metaOptions.fitBounds = {
            bbox: feature.bbox,
            options: bboxOptions,
            event: bboxEvent,
        };
    }

    const hasLayerToFilter = ctx.map.getLayer( `${feature.type}:fill` );

    if ( hasLayerToFilter ) {
        ctx.map.setFilter( `${feature.type}:fill`, null, {
            validate: ctx.debug,
        } );
    }

    const regionFilter = [ '!=', [ 'get', 'id' ], feature.regionID ];

    filterLayerIfExists( ctx.map, `${Region}:fill`, regionFilter, ctx.debug );
    filterLayerIfExists( ctx.map, `${Region}:outline`, regionFilter, ctx.debug );

    const filterIDs = [
        feature.regionID,
    ];

    if ( feature.islandID > 0 ) {
        filterIDs.push( feature.islandID );
    } else {
        filterIDs.push( feature.provinceID );
    }

    const provinceFilter = [
        '!', [
            'match',
            [ 'get', 'id' ],
            filterIDs,
            true,
            false,
        ],
    ];

    filterLayerIfExists( ctx.map, `${Province}:fill`, provinceFilter, ctx.debug );
    filterLayerIfExists( ctx.map, `${Province}:outline`, provinceFilter, ctx.debug );

    deactiveFeaturesForSource( ctx.map, Entity );

    deactiveFeaturesForSource( ctx.map, Neighbourhood );

    deactiveFeaturesForSource( ctx.map, District );

    deactiveFeaturesForSource( ctx.map, Province );

    const parentURL = new URL( '/closestLegacyParent', ctx.apiURL );
    parentURL.searchParams.set( 'id', feature.provinceID );

    const response = await http( parentURL );

    const data = await response.json();

    const parent = LocationFeature.fromJSON( JSON.stringify( data.location ) );

    const currentMunicipalityFeatures = getSourceFeatures( ctx.map, Municipality );

    if ( parent.countOfChildType( Municipality ) !== currentMunicipalityFeatures ) {
        const municipalitiesFilteredToSameParent = currentMunicipalityFeatures.filter( ( f ) => {
            return f.properties.provinceID === feature.provinceID;
        } );

        const src = ctx.map.getSource( Municipality );

        if ( src ) {
            src.setData( {
                type: 'FeatureCollection',
                features: [ ...municipalitiesFilteredToSameParent ],
            } );
        }

        const municipalityOptions = {
            fetchFeatures: true,
            featureSearchURL: new URL( `/${Municipality}`, ctx.apiURL ),
            featureSearchParams: newFeatureFilter( {
                parentType: parent.type,
                parentID: parent.id,
                excludeLocationIDs: [ feature.id ],
            } ),
            fetchProperties: false,
            expectProperties: false,
        };

        const municipalityMeta = {
            focusMapToSearch: false,
            appendFeaturesIfLayerExists: true,
        };

        await ctx.update( municipalityOptions, municipalityMeta, ctx.abortController.signal );
    }

    if ( currentMunicipalityFeatures ) {
        let municipalityFeatures = currentMunicipalityFeatures;

        if ( featureTypeToLoad === Entity || !featureTypeToLoad ) {
            municipalityFeatures = municipalityFeatures.filter( function( m ) {
                return parseInt( m.id ) !== feature.id;
            } );
        }

        if ( featureTypeToLoad === Entity || !featureTypeToLoad ) {
            if ( ctx.map.getLayer( Municipality ) ) {
                ctx.map.setFilter( Municipality, null );
            }

            ctx.map.setFeatureState(
                {
                    source: Municipality,
                    id: feature.id,
                },
                {
                    active: true,
                } );
        }

        for ( let i = 0; i < municipalityFeatures.length; i++ ) {
            ctx.map.setFeatureState(
                {
                    source: Municipality,
                    id: municipalityFeatures[ i ].id,
                },
                {
                    active: false,
                } );
        }
    }

    let featurePath = featureTypeToLoad;

    if  ( !featurePath ) {
        featurePath = Municipality;
    }

    let options = {
        fetchFeatures: true,
        featureSearchURL: new URL( `/${featurePath}`, ctx.apiURL ),
        featureSearchParams: featureParams,
        fetchProperties: true,
        propertySearchURL: ctx.propertySearchURL,
        propertySearchParams: propertyParams,
    };

    // Only load municipalities if we don't currently have any.
    // This is only likely to happen on a "My Location" call.

    const siblings = ctx.map.querySourceFeatures( Municipality, {
        filter: [
            '==', [ 'get', 'provinceID' ], feature.provinceID,
        ],
        validate: ctx.debug,
    } );

    if ( ( !currentMunicipalityFeatures || siblings.length === 0 ) && ( feature.islandID > 0 || feature.provinceID > 0 ) ) {
        let siblingOptions = () => {
            const siblingFeatureFilter = newFeatureFilter();

            if ( feature.islandID > 0 ) {
                siblingFeatureFilter.set( 'parentID', feature.islandID );
                siblingFeatureFilter.set( 'parentType', Island );
            } else {
                siblingFeatureFilter.set( 'parentID', feature.provinceID );
                siblingFeatureFilter.set( 'parentType', Province );
            }

            const disableExcludeSelf = ( 'geolocateSource' in event.detail.originalEvent && event.detail.originalEvent.geolocateSource === true );

            if ( !disableExcludeSelf ) {
                siblingFeatureFilter.set( 'excludeLocationIDs', feature.id );
            }

            const siblingOptions = {
                fetchFeatures: true,
                featureSearchURL: new URL( `/${Municipality}`, ctx.apiURL ),
                featureSearchParams: siblingFeatureFilter,
            };

            const siblingMeta = {
                focusMapToSearch: false,
                appendFeaturesIfLayerExists: true,
            };

            return {
                options: siblingOptions,
                meta: siblingMeta,
            };
        };

        metaOptions.tasks.push( wrapUpdateCall( ctx, siblingOptions ) );
    }

    setFeatureStateIfSourceExists( ctx.map,
        {
            source: feature.type,
            id: feature.id,
        },
        {
            active: true,
        } );

    let results = [];

    try {
        results = await ctx.update( options, metaOptions, ctx.abortController.signal );
    } catch ( err ) {
        if ( err.name === 'AbortError' ) {
            return;
        }
    }

    if ( metaOptions.tasks.length > 0 ) {
        let tasks = [];

        for ( let i = 0; i < metaOptions.tasks.length; i++ ) {
            tasks = [ ...tasks, metaOptions.tasks[ i ] ];
        }

        for ( let t = 0; t < tasks.length; t++ ) {
            const task = tasks[ t ];

            try {
                const result = await task();
                results = [ ...results, ...result ];
            } catch ( err ) {
                if ( err.name === 'AbortError' ) {
                    return;
                }
            }
        }
    }

    ctx.previousSearchState = propertyParams.toString();

    ctx.setLoading( false );

    return results;
};

export {
    handleMunicipalityLayerClick,
};
